<template>
  <div id="calendarMonthList" class="pb-[16px]">
    <div v-for="(month, index) in monthEvents" :key="index">
      <h4
        class="text-[16pt] text-[#3C3A36] tracking-[-0.03em] text-center leading-[1.25rem] font-inter font-black mt-[22px] mb-[8px] md:text-[18pt] md:leading-[1.5rem] lg:text-[20pt] lg:leading-[1.75rem]"
      >
        {{ month.name }}
      </h4>
      <div
        class="grid grid-cols-7 grid-rows-[repeat(6,1fr)] gap-[4px] eventItem overflow-visible relative w-full px-5 md:px-6 lg:px-8"
        :month="month.month"
        :year="month.year"
      >
        <template v-for="date in calendarDays(month.name, eventData)" :key="date.date">
          <div
            class="rounded-[10px] overflow-visible relative"
            :style="{ minHeight: `${getDayHeight(date)}px` }"
            @click="showModal(date)"
            :class="{
              'bg-[#E8DCC766] border-[1px] border-solid': date.isCurrentMonth,
              'bg-[#F2EFED]': !date.isCurrentMonth,
              'bg-[#E8DCC7CC] border-[#6B5744]': date.isCurrentDay,
              'bg-[#E8DCC7CC] border-[#C1B7AE]': !date.isCurrentDay,
            }"
          >
            <div class="flex items-center justify-center pt-[4px]">
              <span
                class="text-[0.625rem] font-medium md:text-[0.6875rem] lg:text-[0.75rem]"
                :class="{
                  'text-[#303036]': date.isCurrentMonth,
                  'text-[#D5CFC8]': !date.isCurrentMonth,
                }"
              >
                {{ ['M', 'T', 'W', 'T', 'F', 'S', 'S'][date.dayOfWeek - 1] }}
              </span>
            </div>

            <div class="flex items-center justify-center">
              <span
                class="text-[0.875rem] leading-[1.25rem] tracking-[-0.6px] md:text-[1rem] md:leading-[1.375rem] lg:text-[1.125rem] lg:leading-[1.5rem]"
                :class="{
                  'text-[#303036]': date.isCurrentMonth,
                  'text-[#D5CFC8]': !date.isCurrentMonth,
                }"
              >
                <span
                  v-if="date.isCurrentDay && date.isCurrentMonth"
                  class="bg-[#6B5744] rounded-full text-white h-[24px] w-[24px] inline-flex items-center justify-center"
                >
                  {{ date.dayOfMonth }}
                </span>
                <span v-else>{{ date.dayOfMonth }}</span>
              </span>
            </div>

            <div
              v-if="date.isCurrentMonth"
              class="px-[4px] absolute top-[44px] left-0 right-0 bottom-0"
            >
              <template v-for="event in date.events" :key="event.id">
                <div
                  v-if="shouldShowEvent(event, date)"
                  class="text-[0.5rem] text-left font-[500] leading-[0.625rem] p-[4px] relative z-10 cursor-grab overflow-hidden md:text-[0.625rem] md:leading-[0.75rem] lg:text-[0.8125rem] lg:leading-[1rem]"
                  :class="getEventClasses(event, date)"
                  :style="getEventStyle(event, date)"
                >
                  <span class="block truncate">{{ event.title }}</span>
                </div>
              </template>

              <!-- "+X more" indicator for hidden events -->
              <div
                v-if="getHiddenEventCount(date) > 0"
                class="text-[0.5rem] text-[#78634F] font-[500] leading-[0.625rem] absolute cursor-pointer md:text-[0.625rem] md:leading-[0.75rem] lg:text-[0.6875rem] lg:leading-[0.875rem]"
                :style="getMoreIndicatorStyle()"
                @click.stop="showModal(date)"
              >
                +{{ getHiddenEventCount(date) }} more
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <ModalSheet v-model="showDayModal" :removeSpace="true">
      <h2 class="text-[1.5rem] text-[#2B2926] leading-[1.5rem] font-[900] mb-[8px]">
        What's happening on
      </h2>
      <h4 class="text-[1rem] text-[#63615E] leading-[1.5rem] font-[600] mb-[24px]">
        {{ activeDate?.monthDay }}
      </h4>
      <EventItem
        v-for="event in activeDate?.events"
        :key="event"
        class="mb-[12px]"
        :title="event.title"
        :description="event.description"
        :event-time-location="event.eventTimeLocation"
        :startDate="event.startDate"
        :endDate="event.endDate"
        :tags="event.tags"
        :link="event.link"
        :buttonText="event.buttonText"
        :isExternal="event.isExternal"
      />
      <Placeholder v-if="activeDate?.events?.length === 0" />
    </ModalSheet>
    <div
      @click="prevDay()"
      v-if="showDayModal"
      class="w-[44px] h-[36px] z-[51] px-[8px] pt-[8px] rounded-[100px] bg-[#3C3A36] fixed bottom-[20px] lg:bottom-[20px] left-6"
    >
      <img class="w-[20px] h-[20px] mx-auto rotate-180" src="/assets/icons/chevron-right.svg" />
    </div>
    <div
      @click="nextDay()"
      v-if="showDayModal"
      class="w-[44px] h-[36px] z-[51] px-[8px] pt-[8px] rounded-[100px] bg-[#3C3A36] fixed bottom-[20px] lg:bottom-[20px] right-6"
    >
      <img class="w-[20px] h-[20px] mx-auto" src="/assets/icons/chevron-right.svg" />
    </div>
  </div>
</template>

<script>
import ModalSheet from '@/components/common/ModalSheet.vue';
import Placeholder from '@/components/common/Placeholder.vue';
import EventItem from './EventItem.vue';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ref } from 'vue';

dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);

// Constants
const DAY_LABELS = 44; // Day of week label + day number height
const MORE_INDICATOR_HEIGHT = 14; // "+X more" indicator height
const BOTTOM_PADDING = 4;
const BUFFER = 8;
const EVENT_GAP = 4; // Gap between event columns
const EVENT_PADDING = 4; // Padding inside event cell
const LANE_GAP = 2; // Pixels between lanes

// Responsive breakpoints (match Tailwind)
const BREAKPOINT_LG = 1024;
const BREAKPOINT_MD = 768;

// Responsive lane configurations
const LANE_CONFIG = {
  desktop: { laneHeight: 20, maxLanes: 4, topOffset: 6, sidePadding: 64 },
  tablet: { laneHeight: 18, maxLanes: 3, topOffset: 4, sidePadding: 48 },
  mobile: { laneHeight: 16, maxLanes: 3, topOffset: 2, sidePadding: 40 },
};

export default {
  data() {
    return {
      currentDate: dayjs(),
      activeDate: null,
      showDayModal: false,
      topMonth: ref(null),
      laneHeight: LANE_CONFIG.mobile.laneHeight,
      maxVisibleLanes: LANE_CONFIG.mobile.maxLanes,
      TOP_OFFSET: LANE_CONFIG.mobile.topOffset,
      LANE_GAP,
    };
  },
  computed: {},
  mounted() {
    this.updateResponsiveConfig();
    window.addEventListener('resize', this.updateResponsiveConfig);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateResponsiveConfig);
  },
  props: {
    months: {
      type: Array,
      default: () => [],
    },
    monthEvents: {
      type: Array,
      default: () => [],
    },
    eventData: {
      type: Array,
      default: () => [],
    },
    scrollElementId: {
      type: String,
    },
  },
  watch: {
    showDayModal(newVal) {
      const scrollElement = document.getElementById(this.scrollElementId);
      if (scrollElement) {
        scrollElement.scrollY = !newVal;
      }
      this.$emit('hideFooter', newVal);
    },
  },
  emits: ['hideFooter'],
  methods: {
    // Helper: Get event duration in days
    getEventDuration(event) {
      const start = dayjs(event.startDate);
      const end = dayjs(event.endDate);
      return end.diff(start, 'day') + 1;
    },

    // Helper: Get current responsive config
    getResponsiveConfig() {
      const width = window.innerWidth;
      if (width >= BREAKPOINT_LG) return LANE_CONFIG.desktop;
      if (width >= BREAKPOINT_MD) return LANE_CONFIG.tablet;
      return LANE_CONFIG.mobile;
    },

    // Helper: Sort events by priority (earlier start, longer duration, called out, title)
    sortEventsByPriority(events) {
      return events.sort((a, b) => {
        if (a.startDate !== b.startDate) {
          return dayjs(a.startDate).isBefore(dayjs(b.startDate)) ? -1 : 1;
        }

        const aDuration = this.getEventDuration(a);
        const bDuration = this.getEventDuration(b);
        if (aDuration !== bDuration) {
          return bDuration - aDuration;
        }

        if (a.isCalledOut !== b.isCalledOut) {
          return a.isCalledOut ? -1 : 1;
        }

        return a.title.localeCompare(b.title);
      });
    },

    // Helper: Assign lanes to events using first-available algorithm
    assignLanesToEvents(events) {
      const occupiedLanes = new Set();
      const eventsWithLanes = [];

      for (const event of events) {
        let lane = 0;
        while (occupiedLanes.has(lane) && lane < this.maxVisibleLanes) {
          lane++;
        }

        if (lane < this.maxVisibleLanes) {
          occupiedLanes.add(lane);
        }

        eventsWithLanes.push({ ...event, stackIndex: lane });
      }

      return eventsWithLanes;
    },

    // Helper: Build event metadata for display
    buildEventMetadata(event, formattedDate, currentDate, dateIndex) {
      const isStart = event.startDate === formattedDate;
      const isEnd = event.endDate === formattedDate;
      const spansMultipleDays = event.startDate !== event.endDate;
      const isLastDayOfWeek = dateIndex % 7 === 6;
      const isWeekBreak = isLastDayOfWeek && !isEnd;

      const eventStartDate = dayjs(event.startDate);
      const eventStartMonth = eventStartDate.month();
      const eventStartYear = eventStartDate.year();
      const eventStartWeek = eventStartDate.week();

      const currentMonth = currentDate.month();
      const currentYear = currentDate.year();
      const currentWeek = currentDate.week();
      const dayOfWeek = currentDate.isoWeekday();

      const isMonthBreak =
        (eventStartMonth !== currentMonth || eventStartYear !== currentYear) &&
        event.startDate !== formattedDate;

      const isWeekBreakStart =
        !isStart &&
        dayOfWeek === 1 &&
        (eventStartWeek !== currentWeek || eventStartYear !== currentYear);

      const isContinuation =
        isEnd && (eventStartMonth !== currentMonth || eventStartYear !== currentYear);

      return {
        ...event,
        isStart,
        isEnd,
        isWeekBreak,
        isWeekBreakStart,
        isMonthBreak,
        isContinuation,
        days: this.getEventDuration(event),
        multipleDays: spansMultipleDays,
        weekRow: Math.floor(dateIndex / 7),
        dateIndex,
      };
    },

    updateResponsiveConfig() {
      const config = this.getResponsiveConfig();
      this.laneHeight = config.laneHeight;
      this.maxVisibleLanes = config.maxLanes;
      this.TOP_OFFSET = config.topOffset;
    },

    findTopVisibleComponent() {
      const components = this.$el.querySelectorAll('.eventItem');
      if (!components.length) return null;

      const headerHeight = 150;
      let candidate = null;

      for (const comp of components) {
        const rect = comp.getBoundingClientRect();
        const isVisible = rect.bottom >= headerHeight && rect.top <= window.innerHeight * 0.4;
        if (isVisible && (!candidate || rect.top < candidate.rectTop)) {
          candidate = { element: comp, rectTop: rect.top };
        }
      }

      const element = candidate ? candidate.element : components[0];
      return {
        month: element.getAttribute('month'),
        year: element.getAttribute('year'),
      };
    },

    prevDay() {
      const prevDate = dayjs(this.activeDate.date, 'YYYY-MM-DD').subtract(1, 'day');
      this.setActiveDate(prevDate);
    },

    nextDay() {
      const nextDate = dayjs(this.activeDate.date, 'YYYY-MM-DD').add(1, 'day');
      this.setActiveDate(nextDate);
    },

    setActiveDate(date) {
      const formattedDate = date.format('YYYY-MM-DD');

      const events = this.eventData
        .filter(event => date.isBetween(event.startDate, event.endDate, 'day', '[]'))
        .sort((a, b) => {
          const aDuration = this.getEventDuration(a);
          const bDuration = this.getEventDuration(b);

          if (aDuration !== bDuration) return bDuration - aDuration;
          if (a.isCalledOut !== b.isCalledOut) return a.isCalledOut ? -1 : 1;
          return a.title.localeCompare(b.title);
        });

      this.activeDate = {
        date: formattedDate,
        monthDay: date.format('DD MMM, dddd'),
        dayOfMonth: date.date(),
        isCurrentDay: date.format('DD/MM/YYYY') === dayjs().format('DD/MM/YYYY'),
        events,
      };
    },

    calendarDays(month, eventData) {
      if (!month) return [];

      const monthDate = dayjs(month, 'MMMM YYYY');
      const firstDayOfMonth = monthDate.startOf('month');
      const lastDayOfMonth = monthDate.endOf('month');
      const startDate = firstDayOfMonth.isoWeekday(1);
      const endDate = lastDayOfMonth.isoWeekday(7);

      const calendar = [];
      let currentDate = startDate;
      let dateIndex = 0;

      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
        const formattedDate = currentDate.format('YYYY-MM-DD');

        // Get and sort events for this day
        const dayEvents = this.sortEventsByPriority(
          eventData?.filter(event =>
            currentDate.isBetween(event.startDate, event.endDate, 'day', '[]'),
          ) || [],
        );

        // Assign lanes to events
        const eventsWithLanes = this.assignLanesToEvents(dayEvents);

        // Build metadata for each event
        const eventsWithMetadata = eventsWithLanes.map(event =>
          this.buildEventMetadata(event, formattedDate, currentDate, dateIndex),
        );

        // Calculate max lanes for day height
        const maxLanes = Math.max(...eventsWithMetadata.map(e => e.stackIndex + 1), 1);

        calendar.push({
          date: formattedDate,
          monthDay: currentDate.format('DD MMM, dddd'),
          dayOfMonth: currentDate.date(),
          dayOfWeek: currentDate.isoWeekday(),
          isCurrentMonth: currentDate.month() === firstDayOfMonth.month(),
          isCurrentDay: currentDate.format('DD/MM/YYYY') === dayjs().format('DD/MM/YYYY'),
          events: eventsWithMetadata,
          maxLanes,
        });

        currentDate = currentDate.add(1, 'day');
        dateIndex++;
      }

      return calendar;
    },

    showModal(date) {
      this.showDayModal = !this.showDayModal;
      this.activeDate = date;
    },

    shouldShowEvent(event) {
      const stackIndex = event.stackIndex ?? 0;
      if (stackIndex >= this.maxVisibleLanes) return false;

      if (!event.multipleDays) return true;

      // Multi-day events: show on start day
      if (event.isStart) return true;

      // Show on end date if at a week/month break
      if (event.isEnd && (event.isWeekBreak || event.isWeekBreakStart || event.isMonthBreak)) {
        return true;
      }

      // Show at week/month boundaries that are not the end date
      if (event.isWeekBreak || event.isWeekBreakStart || event.isMonthBreak) return true;

      // Hide other continuation days
      return false;
    },

    getHiddenEventCount(date) {
      return date.events.filter(event => (event.stackIndex ?? 0) >= this.maxVisibleLanes).length;
    },

    getMoreIndicatorStyle() {
      return {
        top: `${this.TOP_OFFSET + this.maxVisibleLanes * (this.laneHeight + this.LANE_GAP)}px`,
        left: '4px',
        right: '4px',
      };
    },

    getEventSpan(event, date) {
      const dayOfWeek = date.dayOfWeek;
      const daysUntilSunday = 8 - dayOfWeek;
      const eventEndDate = dayjs(event.endDate);
      const currentDate = dayjs(date.date);
      const daysRemaining = eventEndDate.diff(currentDate, 'day') + 1;
      const totalDays = event.days || 1;

      let span = Math.min(daysUntilSunday, daysRemaining, totalDays);

      // Limit span to current month for non-break events
      if (!event.isMonthBreak && !event.isWeekBreakStart) {
        const daysUntilMonthEnd = currentDate.endOf('month').diff(currentDate, 'day') + 1;
        span = Math.min(span, daysUntilMonthEnd);
      }

      return span || 1;
    },

    // Helper: Check if event span reaches end date
    doesSpanReachEndDate(event, date, span) {
      const spanEndDate = dayjs(date.date).add(span - 1, 'day');
      const eventEndDate = dayjs(event.endDate);
      return !spanEndDate.isBefore(eventEndDate, 'day');
    },

    // Helper: Get side padding based on screen size
    getSidePadding() {
      return this.getResponsiveConfig().sidePadding;
    },

    // Helper: Set vertical positioning for event
    setEventVerticalPosition(style, stackIndex) {
      style.position = 'absolute';
      style.top = `${this.TOP_OFFSET + stackIndex * (this.laneHeight + this.LANE_GAP)}px`;
      style.height = `${this.laneHeight}px`;
      style.display = 'flex';
      style.alignItems = 'center';
    },

    // Helper: Check if event is a month break start
    isMonthBreakStart(event, date) {
      const dayOfMonth = dayjs(date.date).date();
      return event.isMonthBreak && (dayOfMonth === 1 || (date.dayOfWeek === 1 && dayOfMonth > 1));
    },

    // Helper: Check if event is a continuation
    isEventContinuation(event, date) {
      const isMonthBreakStart = this.isMonthBreakStart(event, date);
      return (
        (event.multipleDays && !event.isStart && !event.isEnd) ||
        (event.isMonthBreak && !isMonthBreakStart)
      );
    },

    // Helper: Check if event is a spanning start
    isEventSpanningStart(event, date) {
      const isMultiDayStart = event.multipleDays && event.isStart;
      const isMonthBreakStart = this.isMonthBreakStart(event, date);
      const isWeekBreakStart = event.isWeekBreakStart || false;
      return isMultiDayStart || isMonthBreakStart || isWeekBreakStart;
    },

    // Helper: Calculate padding adjustment for spanning events
    calculatePaddingAdjustment(
      isBreakStart,
      spansToNextMonth,
      eventEndsAfterSpan,
      spanEndsAtMonthBoundary,
    ) {
      if (isBreakStart) {
        return spanEndsAtMonthBoundary || !eventEndsAfterSpan ? -EVENT_PADDING : 0;
      }
      if (spansToNextMonth && eventEndsAfterSpan && !spanEndsAtMonthBoundary) {
        return -EVENT_PADDING;
      }
      return -EVENT_PADDING * 2;
    },

    // Helper: Get spanning event style
    getSpanningEventStyle(event, date, stackIndex, span) {
      const style = {};
      this.setEventVerticalPosition(style, stackIndex);
      style.maxWidth = 'none';

      const isMonthBreakStart = this.isMonthBreakStart(event, date);
      const isWeekBreakStart = event.isWeekBreakStart || false;
      const isBreakStart = isMonthBreakStart || isWeekBreakStart;
      style.left = isBreakStart ? '0px' : `${EVENT_PADDING}px`;

      const colWidth = `(100vw - ${this.getSidePadding() + 24}px) / 7`;
      const eventEndDate = dayjs(event.endDate);
      const currentDate = dayjs(date.date);
      const spanEndDate = currentDate.add(span - 1, 'day');

      const spansToNextMonth =
        eventEndDate.month() !== currentDate.month() || eventEndDate.year() !== currentDate.year();
      const eventEndsAfterSpan = eventEndDate.isAfter(spanEndDate);
      const spanEndsAtMonthBoundary =
        spanEndDate.month() !== currentDate.month() || spanEndDate.year() !== currentDate.year();

      const paddingAdjustment = this.calculatePaddingAdjustment(
        isBreakStart,
        spansToNextMonth,
        eventEndsAfterSpan,
        spanEndsAtMonthBoundary,
      );

      style.width = `calc(${span} * ${colWidth} + ${
        span - 1
      } * ${EVENT_GAP}px + ${paddingAdjustment}px)`;

      return style;
    },

    // Helper: Get continuation event style (hidden)
    getContinuationEventStyle() {
      return {
        visibility: 'hidden',
        position: 'absolute',
        width: '0',
        height: '0',
        overflow: 'hidden',
      };
    },

    // Helper: Get single-day or end event style
    getSingleDayEventStyle(stackIndex) {
      const style = {};
      this.setEventVerticalPosition(style, stackIndex);
      style.left = `${EVENT_PADDING}px`;
      style.right = `${EVENT_PADDING}px`;
      return style;
    },

    getEventStyle(event, date) {
      const stackIndex = event.stackIndex ?? 0;
      const span = this.getEventSpan(event, date);

      if (this.isEventSpanningStart(event, date)) {
        return this.getSpanningEventStyle(event, date, stackIndex, span);
      }

      if (this.isEventContinuation(event, date)) {
        return this.getContinuationEventStyle();
      }

      return this.getSingleDayEventStyle(stackIndex);
    },

    // Helper: Set background color classes based on highlight state
    getBackgroundClasses(isHighlighted) {
      if (isHighlighted) {
        return { 'bg-[#FEC629]': true, '!text-[#21201E]': true };
      }
      return { 'bg-[#78634F]': true, 'text-white': true };
    },

    // Helper: Set rounded corner classes based on event position
    getRoundedCornerClasses(event, date) {
      const classes = {};
      const span = this.getEventSpan(event, date);
      const spanReachesEnd = this.doesSpanReachEndDate(event, date, span);

      // Always round right on end date
      if (event.isEnd) {
        classes['rounded-r-[4px]'] = true;
      }

      // Single-day event: round both sides
      if (event.isStart && event.isEnd) {
        classes['rounded-l-[4px]'] = true;
      }
      // Multi-day start: round left, and right if span reaches end
      else if (event.isStart && !event.isEnd) {
        classes['rounded-l-[4px]'] = true;
        if (spanReachesEnd) {
          classes['rounded-r-[4px]'] = true;
        }
      }
      // Month/week break start: round right if span reaches end (no left - it's a continuation)
      else if (event.isMonthBreak || event.isWeekBreakStart) {
        if (spanReachesEnd) {
          classes['rounded-r-[4px]'] = true;
        }
      }
      // Continuation days: no rounded corners

      return classes;
    },

    getEventClasses(event, date) {
      const isHighlighted = event.isHighlighted || event.isCalledOut;
      const backgroundClasses = this.getBackgroundClasses(isHighlighted);
      const roundedClasses = this.getRoundedCornerClasses(event, date);

      return { ...backgroundClasses, ...roundedClasses };
    },

    getDayHeight(_date) {
      const laneGaps = (this.maxVisibleLanes - 1) * this.LANE_GAP;
      return (
        DAY_LABELS +
        this.TOP_OFFSET +
        this.maxVisibleLanes * this.laneHeight +
        laneGaps +
        MORE_INDICATOR_HEIGHT +
        BOTTOM_PADDING +
        BUFFER
      );
    },

    shortTitle(title, eventLength) {
      const maxLength = eventLength > 2 ? 15 : 25;
      return title.slice(0, maxLength) + (title.length > maxLength ? '...' : '');
    },

    getLastDateOfMonth(month) {
      return dayjs(month, 'MMMM YYYY').endOf('month').date();
    },
  },
  components: {
    ModalSheet,
    EventItem,
    Placeholder,
  },
};
</script>
